import React from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'info'
    | 'success'
    | 'warning'
    | 'error'
    | 'ghost'
    | 'link'
    | 'outline'
    | 'neutral';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  shape?: 'default' | 'square' | 'circle';
  loading?: boolean | { text?: string };
  fullWidth?: boolean;
  active?: boolean;
  glass?: boolean;
  noAnimation?: boolean;
  as?: React.ElementType;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size = 'md',
      shape = 'default',
      loading = false,
      fullWidth = false,
      active = false,
      glass = false,
      noAnimation = false,
      as: Component = 'button',
      startIcon,
      endIcon,
      children,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isLoading = !!loading;
    const loadingText = typeof loading === 'object' ? loading.text : undefined;

    const classes = cn(
      'btn',
      'focus-interactive', // Use standardized focus utilities
      // Variants
      variant === 'primary' && 'btn-primary',
      variant === 'secondary' && 'btn-secondary',
      variant === 'accent' && 'btn-accent',
      variant === 'info' && 'btn-info',
      variant === 'success' && 'btn-success',
      variant === 'warning' && 'btn-warning',
      variant === 'error' && 'btn-error',
      variant === 'ghost' && 'btn-ghost',
      variant === 'link' && 'btn-link',
      variant === 'outline' && 'btn-outline',
      variant === 'neutral' && 'btn-neutral',
      // Sizes
      size === 'xs' && 'btn-xs',
      size === 'sm' && 'btn-sm',
      size === 'lg' && 'btn-lg',
      // Shapes
      shape === 'square' && 'btn-square',
      shape === 'circle' && 'btn-circle',
      // States
      active && 'btn-active',
      glass && 'glass',
      noAnimation && 'no-animation',
      fullWidth && 'btn-block',
      className
    );

    return (
      <Component ref={ref} className={classes} disabled={disabled || isLoading} {...props}>
        {isLoading && <span className="loading loading-spinner loading-xs"></span>}
        {startIcon && !isLoading && <span className="btn-start-icon">{startIcon}</span>}
        {loadingText && isLoading ? loadingText : children}
        {endIcon && !isLoading && <span className="btn-end-icon">{endIcon}</span>}
      </Component>
    );
  }
);

Button.displayName = 'Button';
export { Button };
